import { useFragment } from '@apollo/client/react';
import { useMemo } from 'react';

import { TrelloWorkspaceAri } from '@atlassian/ari';
import { getMemberId } from '@trello/authentication';

import { TrelloPlannerWorkspaceIdFragmentDoc } from './TrelloPlannerWorkspaceIdFragment.generated';

interface WorkspaceInfo {
  workspaceAri: string;
  workspaceId: string | null | undefined;
}

/**
 * Custom hook to retrieve the workspace ARI (Atlassian Resource Identifier) and ID for the current member.
 *
 * This hook uses the member's node ID to fetch the planner workspace information and extract both the
 * full workspace ARI and the workspace ID. The return value is memoized to prevent unnecessary re-renders.
 *
 * @returns {WorkspaceInfo} An object containing:
 *   - workspaceAri: The full workspace ARI string (e.g. "ari:cloud:trello::workspace/workspace1")
 *   - workspaceId: The workspace ID extracted from the ARI (e.g. "workspace1")
 *
 * @example
 * const { workspaceAri, workspaceId } = useWorkspaceAri();
 * // workspaceAri: "ari:cloud:trello::workspace/workspace1"
 * // workspaceId: "workspace1"
 */
export const useWorkspaceAri = (): WorkspaceInfo => {
  const memberNodeId = getMemberId({ returnAri: true });

  const { data } = useFragment({
    fragment: TrelloPlannerWorkspaceIdFragmentDoc,
    fragmentName: 'TrelloPlannerWorkspaceId',
    from: {
      __typename: 'TrelloMember',
      id: memberNodeId,
    },
  });

  return useMemo(() => {
    const workspaceAri = data?.planner?.workspace?.id || '';
    // Falls back to '' if the value isn't a valid workspace ARI (e.g. during
    // initial load while `data` is undefined).
    let workspaceId = '';
    if (workspaceAri) {
      try {
        workspaceId = TrelloWorkspaceAri.parse(workspaceAri).workspaceId;
      } catch {
        workspaceId = '';
      }
    }

    return {
      workspaceAri,
      workspaceId,
    };
  }, [data?.planner?.workspace?.id]);
};
